import { printProfileIdFormat } from "@redotech/redo-fulfillment-id";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  ItemOrderBy,
  PageFormat,
  PageOrientation,
} from "./outbound-label-enums";
import type {
  PickListColumn,
  PickListFontSize,
  PickListImageSize,
} from "./pick-list";

export interface ShippingLabelPrintSettings {
  format: PageFormat;
  includePackingSlip: boolean;
  printSlipOnLabelPrinted?: boolean;
  printFast?: boolean;
}

export interface CarrierLabelCustomFieldSettings {
  includeSkus: boolean;
  includeCustomerId: boolean;
  includeOrderNumber?: boolean;
  includeSecondaryOrderName?: boolean;
  includeProductName?: boolean;
}

export interface PackingSlipPrintSettings {
  format: PageFormat;
  orientation: PageOrientation;
  includeFulfillmentBarcode: boolean;
  includeSku: boolean;
  orderBy: ItemOrderBy;
  numCopies?: number | null;
}

export interface PickListPrintSettings {
  format: PageFormat;
  includeImage: boolean;
  orderBy: ItemOrderBy;
  includeBin?: boolean;
  hideOrderNumbers?: boolean;
  includeTote?: boolean | null;
  columns?: PickListColumn[] | null;
  fontSize?: PickListFontSize | null;
  imageSize?: PickListImageSize | null;
  splitQuantityByOrder?: boolean | null;
  showAllCustomAttributes?: boolean | null;
}

export interface PrintSettings {
  shippingLabel?: ShippingLabelPrintSettings;
  packingSlip?: PackingSlipPrintSettings;
  pickList?: PickListPrintSettings;
  commercialInvoice?: { numCopies?: number | null };
}

export const printProfileSettingsSchema = z.object({
  shippingLabel: z.object({
    format: createEnum(PageFormat),
    includePackingSlip: z.boolean(),
    printSlipOnLabelPrinted: z.boolean().nullish(),
  }),
  packingSlip: z.object({
    format: createEnum(PageFormat),
    orientation: createEnum(PageOrientation),
    numCopies: z.number().int().min(1).nullish(),
  }),
  pickList: z.object({ format: createEnum(PageFormat) }),
  commercialInvoice: z.object({ numCopies: z.number().int().min(1).nullish() }),
});
export type PrintProfileSettings = z.infer<typeof printProfileSettingsSchema>;

export const printProfileSchema = z.object({
  id: printProfileIdFormat.schema,
  name: z.string(),
  settings: printProfileSettingsSchema,
});
export type PrintProfile = z.infer<typeof printProfileSchema>;

export interface StoredPrintSettings extends PrintSettings {
  shippingLabel?: ShippingLabelPrintSettings &
    CarrierLabelCustomFieldSettings & {
      includePersonalizedSlip?: boolean;
      includeLogo: boolean;
      condensePages?: boolean;
    };
  packingSlip?: PackingSlipPrintSettings & {
    includeLogo: boolean;
    includeImage: boolean;
    includePrice: boolean;
    printFast?: boolean;
    combineHalfPageSlips?: boolean;
  };
  pickList?: PickListPrintSettings & { inferBinFromSku?: boolean };
  localPickupLabel?: { enabled: boolean };
}

export function resolveDefaultPrintSettings(
  settings: PrintSettings | null | undefined,
): PrintSettings {
  if (settings) {
    return settings;
  }

  return {
    shippingLabel: {
      format: PageFormat.Label,
      includePackingSlip: true,
      printSlipOnLabelPrinted: false,
      printFast: true,
    },
    packingSlip: {
      format: PageFormat.Label,
      orientation: PageOrientation.PORTRAIT,
      includeFulfillmentBarcode: false,
      includeSku: true,
      orderBy: ItemOrderBy.QuantityDesc,
      numCopies: 1,
    },
    pickList: {
      format: PageFormat.Letter,
      includeImage: true,
      includeBin: false,
      hideOrderNumbers: false,
      includeTote: false,
      orderBy: ItemOrderBy.QuantityDesc,
      splitQuantityByOrder: false,
      showAllCustomAttributes: false,
    },
    commercialInvoice: { numCopies: 1 },
  };
}

export function applyPrintProfileSettings(
  defaultSettings: PrintSettings,
  profileSettings: PrintProfileSettings,
): PrintSettings {
  return {
    ...defaultSettings,
    shippingLabel: {
      format: profileSettings.shippingLabel.format,
      includePackingSlip: profileSettings.shippingLabel.includePackingSlip,
      ...(profileSettings.shippingLabel.printSlipOnLabelPrinted != null
        ? {
            printSlipOnLabelPrinted:
              profileSettings.shippingLabel.printSlipOnLabelPrinted,
          }
        : {}),
      ...(defaultSettings.shippingLabel?.printFast !== undefined
        ? { printFast: defaultSettings.shippingLabel.printFast }
        : {}),
    },
    packingSlip: defaultSettings.packingSlip
      ? { ...defaultSettings.packingSlip, ...profileSettings.packingSlip }
      : undefined,
    pickList: defaultSettings.pickList
      ? { ...defaultSettings.pickList, ...profileSettings.pickList }
      : undefined,
    commercialInvoice: {
      ...defaultSettings.commercialInvoice,
      ...profileSettings.commercialInvoice,
    },
  };
}
